import {
  Box,
  List,
  Stack,
  Strong,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  TableCell,
  TableBody,
} from 'braid-design-system';
import type { MDXComponents } from 'mdx/types';
import {
  Children,
  type ComponentProps,
  type ReactNode,
  createContext,
  useContext,
} from 'react';
import { Blockquote, InlineCode, SmartTextLink } from 'scoobie';

import { HorizontalRule } from './HorizontalRule.tsx';
import { MdxCodeBlock } from './MdxCodeBlock.tsx';
import { MdxOrderedList } from './MdxOrderedList.tsx';
import { createSpacedHeading } from './SpacedHeading.tsx';
import { Wrapper } from './Wrapper.tsx';
import { SIZE_TO_SPACE } from './size.ts';

import * as styles from './mdxComponents.css';
import { ScrollableInline } from './ScrollableInline.tsx';

const disableParagraphContext = createContext<boolean>(false);
const preContext = createContext<boolean>(false);

// Don't try to be clever here, this is what you want. No, really. `Text`
// renders inline formatting correctly and fixes the line height. If some
// node is not wrapped in a paragraph and it should be, wrap it using a
// remark plugin, not here.
// TODO: Follow own warning above and consider tree-walking and removing nested paragraphs?
export const makeMdxParagraph =
  (textProps?: Partial<ComponentProps<typeof Text>>) =>
  ({ children }: { children?: ReactNode }) =>
    useContext(disableParagraphContext) ? (
      children
    ) : (
      <DisableParagraph>
        <Text {...textProps}>{children}</Text>
      </DisableParagraph>
    );

const MdxParagraph = makeMdxParagraph();

const DisableParagraph = ({ children }: { children: ReactNode }) => (
  <disableParagraphContext.Provider value={true}>
    {children}
  </disableParagraphContext.Provider>
);

const oidRegex = /^[a-z0-9]+(:[a-z0-9]+)*$/i;

const Code: MDXComponents['code'] = ({ children = '', className, meta }) =>
  useContext(preContext) ? (
    <MdxCodeBlock className={className} meta={meta as string | undefined}>
      {children as string}
    </MdxCodeBlock>
  ) : (
    <Box
      component="span"
      style={{
        whiteSpace:
          // Try to avoid wrapping short strings that will look weird, as do OIDs
          // However, some inline code may need to break to not ruin the whole table
          typeof children === 'string' &&
          (children.length < 20 || oidRegex.test(children))
            ? 'nowrap'
            : 'pre-wrap',
      }}
    >
      <InlineCode>{children}</InlineCode>
    </Box>
  );

// This is unlikely to look attractive, but avoids some horrible casts
const toBraidChildren = (
  children?: ReactNode,
): ComponentProps<typeof Stack>['children'] => {
  if (
    typeof children === 'object' &&
    children !== null &&
    Symbol.iterator in children
  ) {
    return Children.toArray(children)
      .filter((child) => child !== '\n')
      .map(toBraidChildren);
  }

  if (
    typeof children === 'string' ||
    typeof children === 'number' ||
    typeof children === 'bigint'
  ) {
    return <>{children}</>;
  }

  if (children instanceof Promise) {
    return children as never;
  }

  return children;
};

const space = SIZE_TO_SPACE.standard;

const getImageContainerStyle = (colorMode: 'light' | 'dark' | undefined) => {
  switch (colorMode) {
    case 'light':
      return styles.imgContainerLightMode;
    case 'dark':
      return styles.imgContainerDarkMode;
    case undefined:
    default:
      return styles.imgContainer;
  }
};

export const mdxComponents: MDXComponents = {
  a: ({ children, href, title }) => (
    <SmartTextLink href={href ?? ''} title={title}>
      {children}
    </SmartTextLink>
  ),
  blockquote: ({ children }) => (
    <Blockquote>{toBraidChildren(children)}</Blockquote>
  ),
  code: Code,
  h1: createSpacedHeading(1),
  h2: createSpacedHeading(2),
  h3: createSpacedHeading(3),
  h4: createSpacedHeading(4),
  h5: createSpacedHeading(5),
  h6: createSpacedHeading(6),
  hr: HorizontalRule,
  img: ({
    'data-scoobie-style': style,
    'data-color-mode': colorMode,
    ...props
  }) => (
    // For wide SVGs like Mermaid diagrams
    // We use a plain `img` tag inside because we need to pass through the raw
    // `width` and `height` attributes.
    <Box className={getImageContainerStyle(colorMode)}>
      <ScrollableInline>
        <img
          {...props}
          className={styles.img[style as never]}
          style={{
            ...props.style,
            minWidth:
              props.style?.minWidth ??
              (props.width ? Number(props.width) : undefined),
          }}
        />
      </ScrollableInline>
    </Box>
  ),
  li: ({ children }) => (
    <Stack space={space}>{toBraidChildren(children)}</Stack>
  ),
  ol: (props) => (
    <MdxOrderedList {...props}>
      {toBraidChildren(props.children)}
    </MdxOrderedList>
  ),
  p: MdxParagraph,
  pre: (props) => (
    <preContext.Provider value={true}>{props.children}</preContext.Provider>
  ),
  span: (props) => <Box {...props} component="span" />,
  strong: ({ children }) => <Strong>{children}</Strong>,
  table: (props) => (
    <Table
      label="" // 😢 but no way to nicely capture
      {...props}
    >
      {props.children}
    </Table>
  ),
  thead: (props) => <TableHeader {...props}>{props.children}</TableHeader>,
  tbody: (props) => <TableBody {...props}>{props.children}</TableBody>,
  th: (props) => (
    <TableHeaderCell
      {...props}
      wrap
      width="auto"
      minWidth={60}
      align={props.style?.textAlign as never}
    >
      <Box className={styles.tableCell}>{props.children}</Box>
    </TableHeaderCell>
  ),
  tr: (props) => <TableRow {...props}>{props.children}</TableRow>,
  td: (props) => (
    <TableCell
      {...props}
      wrap
      width="auto"
      minWidth={60}
      align={props.style?.textAlign as never}
    >
      <Box className={styles.tableCell}>
        <Stack space="medium">{toBraidChildren(props.children)}</Stack>
      </Box>
    </TableCell>
  ),
  ul: ({ children }) => (
    <List space={space} type="bullet">
      {toBraidChildren(children)}
    </List>
  ),
  MdxFlowElement: DisableParagraph,
  Layout: ({ children }) => <Wrapper>{children}</Wrapper>,
};
